<template>
  <aside class="container-sidebar" :class="{ collapsed: isCollapsed }">
    <nav>
      <div class="wrapper-logo" @click="navigateTo('/ss/dashboard')">
        <img src="/images/logo-sconchat-full.svg" alt="Logo" class="logo white" aria-label="Go home" />
        <img src="/images/logo-sconchat-full-white.svg" alt="Logo" class="logo dark" aria-label="Go home" />
      </div>

      <ul class="menu-list">
        <li v-for="(menu, menuIndex) in SELF_SERVICE_MENU" :key="`menu_${menuIndex}`" class="menu-item">
          <p class="menu-title hide-tablet" v-if="menu.titleKey">
            {{ t(menu.titleKey) }}
          </p>

          <template v-for="(item, itemIndex) in menu.menuItems" :key="`menu_item_${itemIndex}`">
            <div
              v-if="isVisibleMenu(item)"
              :class="['menu-option', { 'menu-selected': isSelectedMenu(item.routePath) }]"
              @click.prevent="onClickMenuSidebar(item)"
            >
              <div class="wrapper-icon">
                <component :is="item.iconComponent" />
              </div>

              <transition name="fade">
                <p v-if="!isCollapsed" class="menu-info">
                  {{ t(item.titleKey) }}
                </p>
                <p v-else class="small sidebar-tooltip">
                  {{ t(item.titleKey) }}
                </p>
              </transition>
            </div>
          </template>
        </li>
      </ul>

      <ul class="profile-section">
        <li class="menu-item">
          <div class="menu-option menu-profile">
            <div class="icon-wrapper">
              <IconProfile
                class="icon-medium"
                :color-profile="profileDetails.colorProfile"
                :initials="profileDetails.initials"
                :is-partner-person="profileDetails.isPartnerPerson"
              />
            </div>

            <transition name="fade">
              <p v-if="!isCollapsed" class="menu-info">
                {{ profileDetails.name }}
              </p>
            </transition>

            <button
              class="button-dim button-rounded button-icon button-icon-small"
              ref="profileButtonRef"
              @click.stop="isVisibleProfileDropdown = !isVisibleProfileDropdown"
            >
              <IconEllipsis class="icon-dim icon-smaller" />
            </button>
          </div>
        </li>
      </ul>
    </nav>
  </aside>

  <Teleport to="body">
    <SelfServiceSettingModal v-if="settingMenu" :menu="settingMenu" @close="toggleSettingModal" />

    <DropdownSide
      v-if="isVisibleProfileDropdown"
      :isVisible="isVisibleProfileDropdown"
      :target-el="profileButtonRef"
      @close="isVisibleProfileDropdown = false"
    >
      <button
        v-for="(menu, menuIndex) in SELF_SERVICE_DROPDOWN_MENU"
        :key="`self_service_menu_${menuIndex}`"
        class="button-medium button-white button-full-width-left"
        @click="onClickMenuOption(menu)"
      >
        <component :is="menu.iconComponent" />
        {{ t(menu.titleKey) }}
      </button>
    </DropdownSide>
  </Teleport>
</template>

<script setup lang="ts">
import IconEllipsis from '@/components/icons/IconEllipsis.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import SelfServiceSettingModal from '@/components/self-service/modal/SelfServiceSettingModal.vue';
import DropdownSide from '@/components/shared/base/DropdownSide.vue';
import { useProfile } from '@/composables/domains/chat/useProfile';
import { useModalCreditHistory } from '@/composables/domains/self-service/useModalCreditHistory';
import { useGtag } from '@/composables/shared/useGtag';
import { useAdminStore } from '@/stores/admin';
import { SocketState, useSocketStore } from '@/stores/socket';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { TypeUser } from '@/types/common';
import { IProfileDetail, ISelfServiceMenuItem, SelfServiceSettingMenu } from '@/types/components';
import { SELF_SERVICE_DROPDOWN_MENU, SELF_SERVICE_MENU } from '@/utils/constants/selfService';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const adminStore = useAdminStore();
const subscriptionStore = useSubscriptionStore();
const socketStore = useSocketStore();

const { getColorProfile, getInitials } = useProfile();
const { openModalCreditHistory } = useModalCreditHistory();
const { trackEventClick } = useGtag();

const isVisibleProfileDropdown = ref<boolean>(false);
const settingMenu = ref<SelfServiceSettingMenu | null>(null);
const profileButtonRef = ref<HTMLElement>();
const isCollapsed = ref<boolean>(false);

const userType = computed<TypeUser>(() => {
  const { userType = '' } = userStore.userInfo || {};
  return userType;
});

const profileDetails = computed<IProfileDetail>(() => {
  return {
    name: adminStore.getPartnerEmployeeInfo.nickname,
    initials: getInitials(adminStore.getPartnerEmployeeInfo.nickname),
    colorProfile: getColorProfile(adminStore.getPartnerEmployeeInfo.userId),
    isPartnerPerson: false,
  };
});

const isSelectedMenu = (routePath: string): boolean => {
  return route.path === routePath;
};

const navigateTo = (path: string, query: Record<string, string> = {}) => {
  if (path) {
    router.push({ path, query });
  }
};

const checkViewport = () => {
  const width = window.innerWidth;

  if (width <= 768) {
    isCollapsed.value = false;
  } else {
    isCollapsed.value = width > 768 && width < 1200;
  }
};

const onClickMenuSidebar = (item) => {
  trackEventClick('side_menu', t(item.titleKey, {}, { locale: 'ko' }));

  switch (item.titleKey) {
    case 'label_extra_users':
      toggleSettingModal('EXTRA_USERS');
      break;
    default:
      navigateTo(item.routePath);
  }
};

const onClickMenuOption = (menu: ISelfServiceMenuItem) => {
  switch (menu.titleKey) {
    case 'button_logout':
      onClickLogout();
      break;
    case 'credit_history':
      openModalCreditHistory();
      break;
    case 'label_edit_my_info':
      toggleSettingModal('PROFILE');
      break;
    default:
      navigateTo(menu.routePath);
  }
};

const toggleSettingModal = (menu: SelfServiceSettingMenu | null = null) => {
  settingMenu.value = menu;
};

const isVisibleMenu = (item: ISelfServiceMenuItem): boolean => {
  return (
    item.accessUserTypes.includes(userType.value) &&
    (item.accessPlanTypes.length === 0 || item.accessPlanTypes.includes(subscriptionStore.currentPlan))
  );
};

const onClickLogout = async () => {
  if (socketStore.socketState !== SocketState.CLOSED) {
    userStore.isPendingLogout = true;
    return;
  }

  await userStore.requestLogout();
  const isHost = adminStore.isHost;

  navigateTo('/login/ss', {
    partnerCode: isHost ? '' : adminStore.getPartnerInfo.partnerCode,
  });
};

onMounted(() => {
  checkViewport();

  window.addEventListener('resize', checkViewport);
  window.addEventListener('orientationchange', checkViewport);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewport);
  window.removeEventListener('orientationchange', checkViewport);
});
</script>

<style scoped lang="scss">
.container-sidebar {
  display: flex;
  flex-direction: column;
  min-width: 15rem;
  width: 15rem;
  max-width: 15rem;
  flex: 0 0 auto;
  height: 100dvh;

  backdrop-filter: $blur-light;
  border-right: 1px solid var(--border-dim);
  z-index: $z-sidebar;
  white-space: nowrap;

  transition:
    width 0.3s ease,
    min-width 0.3s ease,
    padding 0.3s ease;

  @include respond-to(tablet) {
    width: fit-content;
    min-width: auto;

    .menu-option {
      padding: 0.25rem !important;
      gap: 0.5rem;
      max-width: 3.5rem;

      &.menu-profile {
        flex-direction: column;
        padding: 0.75em 0 !important;
      }
    }

    .menu-info {
      display: none;
    }

    .hover-label {
      display: none;
    }

    .hover-label-profile {
      top: 1.5rem;
    }

    .wrapper-logo {
      width: 3.75rem;
    }
  }

  @include respond-to(mobile-landscape) {
    display: none;
  }
}

nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
}

.wrapper-logo {
  position: relative;
  display: flex;

  width: 100%;
  height: fit-content;
  padding: 1rem 1rem 0.75rem 1.25rem;
  overflow: hidden;
  object-fit: contain;
  cursor: pointer;
  flex-shrink: 0;

  .logo {
    height: 2.25rem;
    &.dark {
      display: none;
    }
  }
}

.menu-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.75rem 1rem 0.25rem;
  margin-top: 0.5rem;
  color: var(--text-dim);
  opacity: 0.8;
}

.menu-list {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 2rem 1rem 0;
  overflow-y: auto;
  @include respond-to(tablet) {
    overflow: initial;
  }
}

.profile-section {
  margin-top: auto;
  height: fit-content;
  padding: 0 1rem 1rem;
}

.menu-item {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 2rem;
  &:last-child {
    margin-bottom: 0;
  }
  .menu-title {
    font-size: $font-size-psmall;
    width: fit-content;
    padding-left: 4px;
    margin-bottom: $size-s;
    color: var(--color-gray-100);
    white-space: nowrap;
  }
}

.wrapper-icon {
  height: 2rem;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  :deep(img.on) {
    display: none;
  }
  :deep(img.off) {
    display: block;
  }
}

.menu-option {
  position: relative;
  display: flex;
  justify-self: center;
  align-items: center;
  gap: $size-s;
  padding: 0.625rem 1rem;
  border-radius: $size-s;
  cursor: pointer;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  @media (hover: hover) {
    &:hover {
      .sidebar-tooltip {
        display: block;
      }
    }
  }
  &.menu-selected {
    .wrapper-icon {
      :deep(img.off) {
        display: none;
      }
      :deep(img.on) {
        display: block;
      }
    }
    .menu-info {
      font-weight: 500;
    }
  }
  &:hover,
  &.menu-selected {
    background: $gray10;
    color: var(--text-primary);
    transform: translateX(4px);

    @include respond-to(tablet) {
      transform: translateX(0);
    }
  }

  &.menu-profile {
    cursor: default;
    background: transparent;
    border-radius: $size-s;
    padding: 0.75rem;
    background-color: var(--surface-gray-dimmer);

    &:hover {
      transform: translateX(0);
    }
  }

  .menu-info {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .sidebar-tooltip {
    display: none;
    position: absolute;
    left: 3.5rem;
    background-color: $color-transparent-dark80;
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 100px;
    box-shadow: var(--shadow-toast);
    white-space: nowrap;
    z-index: $z-tooltip;
    pointer-events: none;
  }
}

.hover-label {
  position: absolute;
  top: 50%;
  left: calc(100% + 0.75rem);
  transform: translateY(-50%);
  background-color: var(--surface-glass-dimmest);
  padding: 0.5rem 1rem;
  border-radius: 24px;
  box-shadow: $shadow-toast;
  z-index: $z-floating;
  white-space: nowrap;
  display: none;
}

.menu-item:hover .hover-label {
  display: block;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
